import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import Cookies from 'js-cookie';

const Container = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, ${props => props.theme.colors.background} 0%, ${props => props.theme.colors.secondary} 50%, ${props => props.theme.colors.primary} 100%);
  padding: ${props => props.theme.spacing.lg};
`;

const Card = styled.div`
  background: ${props => props.theme.colors.surface};
  padding: ${props => props.theme.spacing.xxl};
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: ${props => props.theme.shadows.heavy};
  max-width: 450px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  border: 4px solid ${props => props.theme.colors.border.light};
  border-top: 4px solid ${props => props.theme.colors.primary};
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto ${props => props.theme.spacing.lg};
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Message = styled.p`
  color: ${props => props.theme.colors.text.primary};
  font-size: 1.1rem;
  margin: 0;
`;

const ErrorMessage = styled(Message)`
  color: ${props => props.theme.colors.status.error};
`;

const LarkCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);
  const hasCalledRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Use ref to prevent duplicate calls (survives React StrictMode double renders)
      if (hasCalledRef.current) return;
      hasCalledRef.current = true;
      
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code) {
        setError('Authorization code not found');
        toast.error('Failed to authenticate with Lark');
        setTimeout(() => navigate('/login', { replace: true }), 3000);
        return;
      }

      try {
        // Exchange code for access token and user info
        const response = await authAPI.larkCallback(code, state);
        
        if (response.data.access_token && response.data.user) {
          // Store the access token
          Cookies.set('access_token', response.data.access_token, { 
            expires: 31, // 31 days to match JWT expiration
            path: '/', // Available on all pages
            sameSite: 'Lax', // CSRF protection
            secure: window.location.protocol === 'https:' // Secure on HTTPS only
          });
          
          // Update user context
          setUser(response.data.user);
          
          // Show success message
          toast.success(`Welcome back, ${response.data.user.name}!`);
          
          // Redirect to home page immediately
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 500);
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error) {
        console.error('Lark callback error:', error);
        const errorMessage = error.response?.data?.detail || 'Failed to authenticate with Lark';
        setError(errorMessage);
        toast.error(errorMessage);
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    };

    handleCallback();
    // Only run once when component mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      <Card>
        {!error ? (
          <>
            <LoadingSpinner />
            <Message>Authenticating with Lark...</Message>
            <Message style={{ fontSize: '0.9rem', marginTop: '16px', color: '#666' }}>
              Please wait while we verify your credentials
            </Message>
          </>
        ) : (
          <>
            <ErrorMessage>{error}</ErrorMessage>
            <Message style={{ fontSize: '0.9rem', marginTop: '16px' }}>
              Redirecting to login page...
            </Message>
          </>
        )}
      </Card>
    </Container>
  );
};

export default LarkCallbackPage;
